// Exporteert de boorprofielen (SVG) van meerdere boringen naar één PDF: twee naast elkaar, in
// de regel vier per pagina (een diepe boring: twee), de schaal in de voet, de legenda één keer aan
// het eind, en elk profiel op zijn eigen standaardschaal (zie boorprofiel-layout.js), met dezelfde kop en hetzelfde
// bedrijfslogo als het projectrapport. Eén boring (de PDF-knop in de Boorprofiel-tab) staat over
// de volle breedte met zijn eigen legenda.
import { buildBoorprofielData } from './boorprofiel-data.js';
import { scaleLabel } from './boorprofiel-scale.js';
import { layoutBoorprofielPages, layoutBoorprofielRaster, paginaSvg, schaalTekst, LABEL_H, CONTENT_TOP, CONTENT_W, CONTENT_H } from './boorprofiel-layout.js';
import { loadCompanyOrBrandLogo, stampPageHeader, stampPageFooter } from './pdf-logo.js';
import { PAGE } from './pdf-page.js';

/** SVG-string → PNG data-URL via een canvas (met witte achtergrond). Retourneert
 *  { png, w, h } met de intrinsieke SVG-afmetingen (uit de viewBox). Standaard 3 pixels per
 *  SVG-pixel: over de volle A4-breedte ≈ 270 dpi, scherp genoeg voor tekst en arceringen. */
export async function svgToPng(svgString, scale = 3) {
  const vb = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(svgString) || [];
  const w = parseFloat(vb[1]) || 800, h = parseFloat(vb[2]) || 1000;
  const svgSized = svgString.replace('width="100%"', `width="${w}" height="${h}"`);
  const url = URL.createObjectURL(new Blob([svgSized], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const img = new Image();
    await new Promise((res, rej) => { img.onload = res; img.onerror = () => rej(new Error('SVG laden mislukt')); img.src = url; });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale); canvas.height = Math.round(h * scale);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return { png: canvas.toDataURL('image/png'), w, h };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Zet één geschaald boorprofiel (uit scaleForBlock) met "Schaal verticaal 1:N" erboven op de
 * pagina, links op x, met de bovenkant van de schaalstrook op y. Gedeeld met het projectrapport.
 */
export async function drawScaledProfile(doc, scaled, x, y) {
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(70, 70, 70);
  doc.text(scaleLabel(scaled), x + scaled.drawW, y + 2.8, { align: 'right' });
  const { png } = await svgToPng(scaled.svg);
  // 'FAST' = verliesvrije Flate-compressie. Zonder compressie sloeg jsPDF de pixels onbewerkt op:
  // 6,7 MB per profiel, 154 MB voor 23 boringen -- niet te mailen, nauwelijks te openen.
  doc.addImage(png, 'PNG', x, y + LABEL_H, scaled.drawW, scaled.drawH, undefined, 'FAST');
}

/**
 * Bouwt een jsPDF-doc met kant-en-klare boorprofielen (`d`-objecten voor buildBoorprofielSvg),
 * zoveel per pagina als er passen. Ook voor de Boorprofiel-tab, die `d` zelf opbouwt en geen
 * ruwe boring heeft.
 * @param {object[]} profiles  `d`-objecten
 * @param {{ logo?: string|null, label?: string }} [opts]  logo = data-URI van het bedrijfslogo
 *   (anders GeoVoice); label = project, rechts in de kop
 */
export async function buildBoorprofielenDocFromData(profiles, opts = {}) {
  if (!profiles?.length) throw new Error('Geen boringen met lagen om te tekenen.');

  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const logo = await loadCompanyOrBrandLogo(opts.logo);

  if (profiles.length === 1) {
    const { pages } = layoutBoorprofielPages(profiles);
    for (const [pi, page] of pages.entries()) {
      if (pi) doc.addPage('a4', 'portrait');
      stampPageHeader(doc, { title: 'BOORPROFIELEN', label: opts.label, logo });
      stampPageFooter(doc, pi + 1, pages.length);
      for (const { scaled, y } of page) await drawScaledProfile(doc, scaled, PAGE.ML, y);
    }
    return doc;
  }
  const pages = await boorprofielPaginas(profiles);
  for (const [pi, page] of pages.entries()) {
    if (pi) doc.addPage('a4', 'portrait');
    stampPageHeader(doc, { title: 'BOORPROFIELEN', label: opts.label, logo });
    stampPageFooter(doc, pi + 1, pages.length, page.schaal);
    doc.addImage(page.png, 'PNG', PAGE.ML, CONTENT_TOP, CONTENT_W, CONTENT_H, undefined, 'FAST');
  }
  return doc;
}

/**
 * De pagina's van de export als PNG: twee profielen naast elkaar; de schaal als tekst (voor de
 * voet), de legenda op de laatste pagina. Ook voor de bijlage 'Boorprofielen' van het rapport,
 * zodat die er hetzelfde uitziet als de export. ~11 px per mm ≈ 280 dpi.
 * @returns {Promise<Array<{png:string, namen:string[], schaal:string, legenda:boolean}>>}
 */
export async function boorprofielPaginas(profiles) {
  const { pages, legenda } = layoutBoorprofielRaster(profiles);
  const uit = [];
  for (const page of pages) {
    const { png } = await svgToPng(paginaSvg(page, legenda), 11);
    uit.push({ png, namen: page.cells.map((c) => c.d.header?.name || '').filter(Boolean), schaal: schaalTekst(page), legenda: page.legendaY != null });
  }
  return uit;
}

/**
 * Bouwt een jsPDF-doc met de boorprofielen (zoveel per pagina als er passen) — zonder op te slaan,
 * zodat de Export-tab het ook als voorvertoning (via pdf.js) kan renderen.
 * @param {object[]} rawBorings  Platte boringen (API-vorm) incl. layers/peilbuizen/afwerking.
 * @param {{ heightDatum?: string, logo?: string|null, label?: string }} [opts]
 */
export async function buildBoorprofielenDoc(rawBorings, opts = {}) {
  const profiles = (rawBorings || []).map(raw => buildBoorprofielData(raw, opts)).filter(Boolean);
  return buildBoorprofielenDocFromData(profiles, opts);
}

/** Bouwt de boorprofielen-PDF en downloadt 'm. */
export async function exportBoorprofielenPdf(rawBorings, opts = {}, filename = 'boorprofielen.pdf') {
  const doc = await buildBoorprofielenDoc(rawBorings, opts);
  doc.save(filename);
}
